import React, {useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {useUniqPublicTransportRouteNames} from "../hooks/use_uniq_public_transport_route_names";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";

interface IProps {
    busRoutes?: IPublicTransportRoute[] | null;
    railwayRoutes?: IPublicTransportRoute[] | null;
    subwayRoutes?: IPublicTransportRoute[] | null;
    tramRoutes?: IPublicTransportRoute[] | null;
    activePoiRoute?: IPublicTransportRoute | null;
    onTransportRouteClick: (publicTransportRoute: IPublicTransportRoute) => void;
    onExpand?: () => void;
}

const ADDITIONAL_ROUTES_FROM_INDEX = 25;

const getTitleForRoute = (route: IPublicTransportRoute) => {
    return `Od ${route.from} do ${route.to}`;
};

const isRouteActive = (route?: IPublicTransportRoute, activePoiRoute?: IPublicTransportRoute | null) => {
    return !!route && !!activePoiRoute && route?.id === activePoiRoute?.id;
};

export const RegionTransportRoutes = (props: IProps) => {
    const [showAdditionalRoutes, setShowAdditionalRoutes] = useState(false);

    const routeBackground = (transportType: keyof typeof transportTypeBackgroundStyles, route?: IPublicTransportRoute) => {
        return isRouteActive(route, props.activePoiRoute) ? activeRouteBackgroundStyle : transportTypeBackgroundStyles[transportType];
    };

    const busRoutesRaw = useUniqPublicTransportRouteNames(props.busRoutes);
    const busRoutes = useMemo(() => {
        return busRoutesRaw?.sort((firstRoute, secondRoute) => {
            const isFirstRouteNightRoute = firstRoute.name[0] === "N";
            const isSecondRouteNightRoute = secondRoute.name[0] === "N";

            if ((isFirstRouteNightRoute && isSecondRouteNightRoute) || (!isFirstRouteNightRoute && !isSecondRouteNightRoute)) {
                return 0;
            }

            return isFirstRouteNightRoute ? 1 : -1;
        });
    }, [busRoutesRaw]);
    const subwayRoutes = useUniqPublicTransportRouteNames(props.subwayRoutes);
    const tramRoutes = useUniqPublicTransportRouteNames(props.tramRoutes);
    const railwayRoutes = useUniqPublicTransportRouteNames(props.railwayRoutes);

    const subwayRoutesFrom = subwayRoutes?.length || 0;
    const railwayRoutesLength = railwayRoutes?.length || 0;
    const tramRoutesLength = tramRoutes?.length || 0;
    const busRoutesLength = busRoutes?.length || 0;
    const totalRoutesLength = subwayRoutesFrom + railwayRoutesLength + tramRoutesLength + busRoutesLength;

    const routeIndexNormalization = {
        subway: 0,
        railway: subwayRoutesFrom,
        tram: subwayRoutesFrom + railwayRoutesLength,
        bus: subwayRoutesFrom + railwayRoutesLength + tramRoutesLength
    };

    const isRouteVisible = (type: keyof typeof routeIndexNormalization, index: number) => {
        return showAdditionalRoutes || routeIndexNormalization[type] + index < ADDITIONAL_ROUTES_FROM_INDEX;
    };

    const toggleShowAdditionalRoutes = () => {
        if (!showAdditionalRoutes) {
            props.onExpand?.();
        }

        setShowAdditionalRoutes((prevState) => !prevState);
    };

    return (
        <div>
            <ul className={listWrapperStyle}>
                {subwayRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("subway", route), isRouteVisible("subway", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {railwayRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("railway", route), isRouteVisible("railway", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {tramRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("tram", route), isRouteVisible("tram", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {busRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("bus", route), isRouteVisible("bus", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}
            </ul>

            {totalRoutesLength > ADDITIONAL_ROUTES_FROM_INDEX ? (
                <Text as="div" variant="info_txt_1" className={showAdditionalRoutesButtonStyle} onClick={toggleShowAdditionalRoutes}>
                    {showAdditionalRoutes ? "Pokaż mniej linii komunikacyjnych" : "Pokaż więcej linii komunikacyjnych"}
                </Text>
            ) : null}
        </div>
    );
};

const listWrapperStyle = css({
    paddingLeft: 0,
    listStyle: "none",
    display: "flex",
    alignItems: "normal",
    justifyContent: "normal",
    marginTop: 0.5,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    flexWrap: "wrap",
    gap: 0.5
});

const routeStyle = css({
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 1,
    paddingLeft: 1,
    paddingRight: 1,
    height: "2.4rem",
    cursor: "pointer"
});

const visibleRouteStyle = css({
    display: "flex"
});

const hiddenRouteStyle = css({
    display: "none"
});

const routeIconStyle = css({
    paddingRight: 1
});

const activeRouteBackgroundStyle = css({
    backgroundColor: "var(--colors-primary)"
});

const transportTypeBackgroundStyles = {
    bus: css({backgroundColor: "var(--colors-info)"}),
    railway: css({backgroundColor: "var(--colors-transport-railway, #BCAFE1)"}),
    subway: css({backgroundColor: "var(--colors-warning)"}),
    tram: css({backgroundColor: "var(--colors-success)"})
};

const showAdditionalRoutesButtonStyle = css({
    marginTop: 1,
    textDecoration: "underline",
    cursor: "pointer"
});
import React, {useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {useUniqPublicTransportRouteNames} from "../hooks/use_uniq_public_transport_route_names";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";

interface IProps {
    busRoutes?: IPublicTransportRoute[] | null;
    railwayRoutes?: IPublicTransportRoute[] | null;
    subwayRoutes?: IPublicTransportRoute[] | null;
    tramRoutes?: IPublicTransportRoute[] | null;
    activePoiRoute?: IPublicTransportRoute | null;
    onTransportRouteClick: (publicTransportRoute: IPublicTransportRoute) => void;
    onExpand?: () => void;
}

const ADDITIONAL_ROUTES_FROM_INDEX = 25;

const getTitleForRoute = (route: IPublicTransportRoute) => {
    return `Od ${route.from} do ${route.to}`;
};

const isRouteActive = (route?: IPublicTransportRoute, activePoiRoute?: IPublicTransportRoute | null) => {
    return !!route && !!activePoiRoute && route?.id === activePoiRoute?.id;
};

export const RegionTransportRoutes = (props: IProps) => {
    const [showAdditionalRoutes, setShowAdditionalRoutes] = useState(false);

    const routeBackground = (transportType: keyof typeof transportTypeBackgroundStyles, route?: IPublicTransportRoute) => {
        return isRouteActive(route, props.activePoiRoute) ? activeRouteBackgroundStyle : transportTypeBackgroundStyles[transportType];
    };

    const busRoutesRaw = useUniqPublicTransportRouteNames(props.busRoutes);
    const busRoutes = useMemo(() => {
        return busRoutesRaw?.sort((firstRoute, secondRoute) => {
            const isFirstRouteNightRoute = firstRoute.name[0] === "N";
            const isSecondRouteNightRoute = secondRoute.name[0] === "N";

            if ((isFirstRouteNightRoute && isSecondRouteNightRoute) || (!isFirstRouteNightRoute && !isSecondRouteNightRoute)) {
                return 0;
            }

            return isFirstRouteNightRoute ? 1 : -1;
        });
    }, [busRoutesRaw]);
    const subwayRoutes = useUniqPublicTransportRouteNames(props.subwayRoutes);
    const tramRoutes = useUniqPublicTransportRouteNames(props.tramRoutes);
    const railwayRoutes = useUniqPublicTransportRouteNames(props.railwayRoutes);

    const subwayRoutesFrom = subwayRoutes?.length || 0;
    const railwayRoutesLength = railwayRoutes?.length || 0;
    const tramRoutesLength = tramRoutes?.length || 0;
    const busRoutesLength = busRoutes?.length || 0;
    const totalRoutesLength = subwayRoutesFrom + railwayRoutesLength + tramRoutesLength + busRoutesLength;

    const routeIndexNormalization = {
        subway: 0,
        railway: subwayRoutesFrom,
        tram: subwayRoutesFrom + railwayRoutesLength,
        bus: subwayRoutesFrom + railwayRoutesLength + tramRoutesLength
    };

    const isRouteVisible = (type: keyof typeof routeIndexNormalization, index: number) => {
        return showAdditionalRoutes || routeIndexNormalization[type] + index < ADDITIONAL_ROUTES_FROM_INDEX;
    };

    const toggleShowAdditionalRoutes = () => {
        if (!showAdditionalRoutes) {
            props.onExpand?.();
        }

        setShowAdditionalRoutes((prevState) => !prevState);
    };

    return (
        <div>
            <ul className={listWrapperStyle}>
                {subwayRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("subway", route), isRouteVisible("subway", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {railwayRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("railway", route), isRouteVisible("railway", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {tramRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("tram", route), isRouteVisible("tram", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}

                {busRoutes.map((route, index) => (
                    <li
                        key={route.id}
                        className={cx(routeStyle, routeBackground("bus", route), isRouteVisible("bus", index) ? visibleRouteStyle : hiddenRouteStyle)}
                        title={getTitleForRoute(route)}
                        onClick={() => props.onTransportRouteClick(route)}
                    >
                        <PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="transparent" className={routeIconStyle} />
                        <Text as="span" variant="info_txt_2" strong>
                            {route.name}
                        </Text>
                    </li>
                ))}
            </ul>

            {totalRoutesLength > ADDITIONAL_ROUTES_FROM_INDEX ? (
                <Text as="div" variant="info_txt_1" className={showAdditionalRoutesButtonStyle} onClick={toggleShowAdditionalRoutes}>
                    {showAdditionalRoutes ? "Pokaż mniej linii komunikacyjnych" : "Pokaż więcej linii komunikacyjnych"}
                </Text>
            ) : null}
        </div>
    );
};

const listWrapperStyle = css({
    paddingLeft: 0,
    listStyle: "none",
    display: "flex",
    alignItems: "normal",
    justifyContent: "normal",
    marginTop: 0.5,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    flexWrap: "wrap",
    gap: 0.5
});

const routeStyle = css({
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 1,
    paddingLeft: 1,
    paddingRight: 1,
    height: "2.4rem",
    cursor: "pointer"
});

const visibleRouteStyle = css({
    display: "flex"
});

const hiddenRouteStyle = css({
    display: "none"
});

const routeIconStyle = css({
    paddingRight: 1
});

const activeRouteBackgroundStyle = css({
    backgroundColor: "var(--colors-primary)"
});

const transportTypeBackgroundStyles = {
    bus: css({backgroundColor: "var(--colors-info)"}),
    railway: css({backgroundColor: "var(--colors-transport-railway, #BCAFE1)"}),
    subway: css({backgroundColor: "var(--colors-warning)"}),
    tram: css({backgroundColor: "var(--colors-success)"})
};

const showAdditionalRoutesButtonStyle = css({
    marginTop: 1,
    textDecoration: "underline",
    cursor: "pointer"
});
